import React, { useRef, useEffect, useState } from "react";
import { useSearch } from "@/hooks/useSearch";
import { usePartnerBooks } from "@/hooks/usePartnerBooks";
import ConversationHeader from "@/components/ConversationHeader";
import HeroSection from "@/components/HeroSection";
import BottomSearchBar from "@/components/BottomSearchBar";
import ResultCard from "@/components/ResultCard";
import { cn } from "@/lib/utils";

const Index = () => {
  // Charge les flux partenaires au montage de l'application
  usePartnerBooks();

  const {
    question,
    answerSize,
    isLoading,
    loadingType,
    answer,
    hits,
    cost,
    lastActionType,
    hasQueried,
    hasResults,
    setQuestion,
    setAnswerSize,
    handleAction,
    reset,
  } = useSearch();

  const resultsRef = useRef<HTMLDivElement>(null);
  const [resultKey, setResultKey] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Increment key when new results arrive to trigger re-animation
  useEffect(() => {
    if (hasResults && !isLoading) {
      setResultKey((prev) => prev + 1);
      setIsTransitioning(false);
    }
  }, [answer, hits, hasResults, isLoading]);

  // Set transitioning state when loading starts
  useEffect(() => {
    if (isLoading && hasQueried) {
      setIsTransitioning(true);
    }
  }, [isLoading, hasQueried]);

  // Scroll to results when they appear
  useEffect(() => {
    if (hasQueried && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hasQueried]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Fixed header - only visible after first query */}
      {hasQueried && <ConversationHeader onReset={reset} />}

      {/* Hero Section - Only visible before first query */}
      {!hasQueried && (
        <HeroSection
          question={question}
          onQuestionChange={setQuestion}
          isLoading={isLoading}
          loadingType={loadingType}
          answerSize={answerSize}
          onAnswerSizeChange={setAnswerSize}
          onSearch={() => handleAction("find")}
          onAnalyze={() => handleAction("analyze")}
        />
      )}

      {/* Results area - visible after first query */}
      {hasQueried && (
        <main className="flex-1 pt-16 pb-32">
          <div ref={resultsRef} className="max-w-3xl mx-auto px-4 py-6">
            {/* Loading state */}
            {isLoading && !answer && (
                <div className="flex items-center justify-center py-12 animate-fade-in">
                  <div className="text-muted-foreground">
                    {loadingType === 'analyze' ? 'Réflexion en cours...' : 'Recherche en cours...'}
                  </div>
                </div>
              )}

            {/* Results with smooth transition */}
            {hasResults && (!isLoading || (isLoading && answer)) && (
              <div>
                <ResultCard
                  answer={answer}
                  hits={hits}
                  actionType={lastActionType || undefined}
                  cost={cost}
                  question={question}
                  isLoading={isLoading}
                />
              </div>
            )}
          </div>
        </main>
      )}

      {/* Fixed bottom search bar - only visible after first query */}
      {hasQueried && (
        <BottomSearchBar
          question={question}
          onQuestionChange={setQuestion}
          isLoading={isLoading}
          loadingType={loadingType}
          answerSize={answerSize}
          onAnswerSizeChange={setAnswerSize}
          onSearch={() => handleAction("find")}
          onAnalyze={() => handleAction("analyze")}
        />
      )}
    </div>
  );
};

export default Index;
